import axios from 'axios';
import { xorDecrypt } from '../encrypt';
import { BASE_API_URL } from "../constants";

const getToken = () => {
  const encryptedToken = localStorage.getItem("ABAtoken");
  return xorDecrypt(encryptedToken); // Decrypt the token
};

export const fetchSupervisionSessionGoals = async () => {
  const response = await axios.post(`${BASE_API_URL}/supervision-session-goals/getall`, {}, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
  });
  return response.data;
};

export const fetchFeedbacks = async () => {
  const response = await axios.post(`${BASE_API_URL}/feedbacks/getall`, {}, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
  });
  return response.data;
};

export const fetchSupervisionNextSessionPlans = async () => {
  const response = await axios.post(`${BASE_API_URL}/session-plans/getall`, {}, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
  });
  return response.data;
};



const sessionGoalAddressed = {
  "Implementation of Behavior Intervention Plan (BIP)": {
    focus:
      "The BCBA monitors how effectively the BT/RBT is implementing the Behavior Intervention Plan (BIP), including the specific strategies and techniques outlined for managing challenging behaviors.",
    example:
      "Observing if the BT/RBT uses the correct prompting strategy and follows through with the extinction procedure as outlined in the plan.",
  },
  "Adherence to Treatment Protocols": {
    focus:
      "The BCBA ensures that the BT/RBT is following the treatment protocols accurately and consistently, including skill acquisition programs, reinforcement schedules, and data collection procedures.",
    example:
      "Checking if the BT/RBT is following the task analysis steps correctly when teaching a new skill like handwashing.",
  },
  "Data Collection and Accuracy": {
    focus:
      "The BCBA supervises the BT/RBT’s data collection process, ensuring that it is accurate, reliable, and completed in real-time.",
    example:
      "Reviewing the frequency data for a specific behavior or assessing the accuracy of recorded trial-by-trial data.",
  },
  "Skill Acquisition Programs": {
    focus:
      "The BCBA observes how the BT/RBT is teaching new skills, ensuring the use of evidence-based techniques such as Discrete Trial Training (DTT), Natural Environment Teaching (NET), or Pivotal Response Training (PRT).",
    example:
      "Monitoring if the BT/RBT provides correct reinforcement for a child’s independent response during a teaching session.",
  },
  "Use of Prompting and Prompt Fading": {
    focus:
      "The BCBA assesses the BT/RBT’s use of prompts, ensuring they are appropriate for the client’s learning level and are faded effectively over time to promote independence.",
    example:
      "Ensuring that the BT/RBT uses a least-to-most prompting strategy and fades the prompts gradually as the child gains confidence in the skill.",
  },
  "Behavior Management and De-escalation Techniques": {
    focus:
      "The BCBA supervises how the BT/RBT responds to challenging behaviors, ensuring that they use appropriate behavior management strategies and follow the crisis intervention plan if necessary.",
    example:
      "Observing the BT/RBT using differential reinforcement to address a child’s aggression or using calming strategies to de-escalate a tantrum.",
  },
  "Therapeutic Relationship and Rapport Building": {
    focus:
      "The BCBA evaluates the BT/RBT’s ability to build a positive, trusting relationship with the client, which is essential for effective therapy.",
    example:
      "Noticing if the BT/RBT engages with the child in a playful manner and uses the child’s preferred items or activities to build rapport.",
  },
  "Generalization and Maintenance of Skills": {
    focus:
      "The BCBA checks whether the BT/RBT is helping the client generalize newly acquired skills across different environments, people, and contexts.",
    example:
      "Observing if the BT/RBT practices a skill like requesting help not only at the table but also during playtime and transitions.",
  },
  "Parent and Caregiver Involvement": {
    focus:
      "The BCBA may supervise how the BT/RBT communicates with the child’s parents or caregivers, involving them in the session when appropriate.",
    example:
      "Ensuring the BT/RBT provides parents with updates on the session and demonstrates strategies that parents can practice at home.",
  },
  "Adherence to Ethical Guidelines": {
    focus:
      "The BCBA ensures that the BT/RBT follows ethical guidelines, including maintaining client dignity, obtaining informed consent, and respecting client confidentiality.",
    example:
      "Observing that the BT/RBT does not use any aversive procedures and treats the client with respect throughout the session.",
  },
  "Use of Reinforcement Strategies": {
    focus:
      "The BCBA checks if the BT/RBT is using appropriate reinforcement strategies, such as delivering reinforcement immediately and using varied, preferred reinforcers.",
    example:
      "Ensuring the BT/RBT uses a mix of verbal praise, tokens, or access to preferred toys to reinforce desired behaviors.",
  },
  "Assessment of Client Progress": {
    focus:
      "The BCBA assesses the client’s progress toward their treatment goals based on the session data and observations of the BT/RBT’s implementation of programs.",
    example:
      "Reviewing the client’s progress on a specific skill acquisition goal and making adjustments if the client is not meeting targets.",
  },
  "Training on New Programs or Procedures": {
    focus:
      "The BCBA may use the supervision session to introduce new programs, procedures, or changes to the treatment plan, training the BT/RBT on how to implement them effectively.",
    example:
      "Demonstrating a new task analysis for a self-care skill and providing guidance on how to start teaching it.",
  },
  "Quality Assurance and Treatment Fidelity": {
    focus:
      "The BCBA ensures the overall quality of the session, checking that all interventions are implemented with high fidelity to the treatment plan.",
    example:
      "Conducting fidelity checks using a checklist or observation form to measure the BT/RBT’s adherence to specific protocols.",
  },
  Others: {
    focus:
      "Miscellaneous goals or activities specific to the client or program needs.",
    example:
      "Any additional observations or training tasks not categorized above.",
  },
};

const feedbackProvided = {
  "Reinforcing Positive Behavior and Skills": {
    feedbackExample:
      "Great job using positive reinforcement consistently during the session! I noticed that you delivered praise immediately after the correct response, which helped keep the child engaged.",
    purpose:
      "To acknowledge and reinforce effective techniques used by the BT/RBT, encouraging them to continue using these skills.",
  },
  "Encouragement to Increase Engagement with the Client": {
    feedbackExample:
      "You did a nice job building rapport at the start of the session, but I’d like to see more engagement throughout the entire session. Try incorporating more playful interactions and using the client’s preferred items to keep them motivated.",
    purpose:
      "To enhance the BT/RBT’s ability to connect with the client and maintain a positive, engaging environment.",
  },
  "Suggestions for Improving Data Collection Accuracy": {
    feedbackExample:
      "I noticed that some of the data points were missing during the session. Let’s make sure we’re recording every trial accurately, even if the client’s response is incorrect. This will help us track progress more effectively.",
    purpose:
      "To emphasize the importance of accurate data collection and guide the BT/RBT in improving their recording practices.",
  },
  "Guidance on Prompting Techniques": {
    feedbackExample:
      "You used hand-over-hand prompting well, but remember to fade the prompts gradually. Let’s try using a verbal prompt first next time to see if the client can respond with less assistance.",
    purpose:
      "To help the BT/RBT understand when and how to use different prompting levels and strategies effectively.",
  },
  "Recommendations for Reinforcement Strategies": {
    feedbackExample:
      "You did a great job identifying the child’s preferred reinforcers. However, try varying the type of reinforcement to keep it exciting for the client. Instead of using the same toy repeatedly, mix in social praise or a different item they like.",
    purpose:
      "To encourage the use of varied and effective reinforcement strategies to maintain the client’s motivation.",
  },
  "Feedback on Session Pace and Flow": {
    feedbackExample:
      "The session pace was a bit slow today, especially during the skill acquisition tasks. Let’s try to keep a steady flow by reducing the wait time between trials.",
    purpose:
      "To help the BT/RBT improve the efficiency of the session, keeping the client engaged and reducing downtime.",
  },
  "Suggestions for Managing Challenging Behaviors": {
    feedbackExample:
      "You handled the client’s tantrum well by staying calm, but I noticed that you gave attention during the behavior. Let’s try using planned ignoring next time, and reinforce the child when they calm down.",
    purpose:
      "To provide constructive feedback on how to manage problem behaviors more effectively, ensuring the BT/RBT uses consistent, evidence-based strategies.",
  },
  "Clarification on Treatment Protocols": {
    feedbackExample:
      "You did well with the discrete trial training, but I noticed some confusion with the task analysis steps for handwashing. Let’s review the steps together to ensure consistency in the next session.",
    purpose:
      "To correct any misunderstandings or inconsistencies in the implementation of the treatment plan.",
  },
  "Encouragement to Improve Professional Communication": {
    feedbackExample:
      "You did a great job updating the parents about the session. For next time, let’s focus on using clear, non-technical language so they can better understand the strategies we’re using.",
    purpose:
      "To enhance the BT/RBT’s ability to communicate effectively with parents, caregivers, and other team members.",
  },
  "Feedback on Adherence to Ethical Guidelines": {
    feedbackExample:
      "I noticed that you allowed the client to go without a break for an extended period. Remember, it’s important to follow the scheduled breaks in the plan to respect the client’s needs and maintain their dignity.",
    purpose:
      "To ensure the BT/RBT adheres to ethical guidelines and maintains high standards of client care.",
  },
  "Suggestions for Generalization of Skills": {
    feedbackExample:
      "You did a great job teaching the skill at the table. Next time, let’s practice this skill in different settings, like during playtime, to help the client generalize it across environments.",
    purpose:
      "To encourage the BT/RBT to work on generalizing the skills being taught, ensuring the child can use them in various contexts.",
  },
  "Positive Reinforcement for Professional Growth": {
    feedbackExample:
      "I can see a lot of improvement in how you handle transitions. You were proactive and used visual supports effectively. Keep up the great work!",
    purpose:
      "To recognize the BT/RBT’s growth and reinforce their progress, boosting their confidence and morale.",
  },
  "Guidance on Time Management During Sessions": {
    feedbackExample:
      "I noticed we ran out of time for the last program. Let’s focus on managing the time more effectively by setting a timer for each task so we can cover all the planned activities.",
    purpose:
      "To help the BT/RBT improve their time management skills, ensuring all parts of the session are completed.",
  },
  "Recommendations for More Effective Use of Visual Supports": {
    feedbackExample:
      "The visual schedule worked well for the client today. In the future, let’s try adding more pictures or icons for each activity to give the client clearer cues.",
    purpose:
      "To optimize the use of visual supports, making them more effective for the client.",
  },
  "Constructive Feedback on Professional Demeanor": {
    feedbackExample:
      "You maintained a positive and calm demeanor throughout the session, even when the client was upset. This helped create a supportive environment. Keep focusing on staying calm and patient, as it sets a great example for the client.",
    purpose:
      "To acknowledge and reinforce the BT/RBT’s professional behavior and approach, which contributes to a positive therapeutic environment.",
  },
  Others: {
    focus:
      "Miscellaneous goals or activities specific to the client or program needs.",
    example:
      "Any additional observations or training tasks not categorized above.",
  },
};

const nextSessionPlans = {
  "Review of Feedback and Implementation Practice": {
    plan: "The next session will focus on reviewing the feedback given during the supervision and practicing any strategies that need improvement.",
    goal: "To ensure that the BT/RBT has a clear understanding of the feedback and can implement the strategies effectively.",
  },
  "Introduction of New Skill Acquisition Programs": {
    plan: "Introduce a new skill acquisition program based on the client’s current needs and readiness.",
    goal: "To expand the client’s skill set and address newly identified areas for development (e.g., teaching self-help skills like dressing).",
  },
  "Adjustment of Behavior Intervention Plan (BIP)": {
    plan: "Update the Behavior Intervention Plan (BIP) based on the client’s response to the current strategies. Modify or add interventions as needed.",
    goal: "To increase the effectiveness of the behavior management strategies and address any persistent or new challenging behaviors.",
  },
  "Enhanced Focus on Generalization of Skills": {
    plan: "Increase the focus on helping the client generalize learned skills across different settings and with different people.",
    goal: "To ensure the client can apply the skills in various contexts, improving functional use.",
  },
  "Additional Training on Data Collection": {
    plan: "Provide a targeted session on data collection techniques for the BT/RBT, including practice with real-time data recording.",
    goal: "To improve the accuracy and consistency of data collection, which is crucial for monitoring progress.",
  },
  "Increased Emphasis on Parent Involvement": {
    plan: "Incorporate more parent training or involve the parents during the next session to demonstrate strategies and encourage practice at home.",
    goal: "To ensure consistency in the application of ABA strategies across different environments and reinforce learning at home.",
  },
  "Role-Playing and Practice Scenarios": {
    plan: "Conduct role-playing exercises with the BT/RBT to practice responding to specific client behaviors or teaching a new skill.",
    goal: "To build the BT/RBT’s confidence and competence in handling challenging situations and implementing new teaching techniques.",
  },
  "Increasing the Complexity of Programs": {
    plan: "Increase the complexity of the existing programs by adding more steps, reducing prompts, or increasing the criteria for mastery.",
    goal: "To challenge the client appropriately and continue their skill development.",
  },
  "Implementation of a New Reinforcement System": {
    plan: "Introduce a new or modified reinforcement system, such as a token economy, based on the client’s preferences and current motivation levels.",
    goal: "To maintain the client’s engagement and motivation during the sessions.",
  },
  "Focused Behavior Management Training": {
    plan: "Provide targeted training to the BT/RBT on specific behavior management strategies that need improvement, such as using extinction or implementing planned ignoring.",
    goal: "To increase the BT/RBT’s ability to handle challenging behaviors effectively and consistently.",
  },
  "Skill Maintenance and Fluency Practice": {
    plan: "Incorporate sessions focused on maintaining previously acquired skills and building fluency through repeated practice.",
    goal: "To ensure the client retains learned skills and performs them quickly and accurately.",
  },
  "Collaboration with Other Service Providers": {
    plan: "Coordinate with the client’s other service providers (e.g., speech therapists, occupational therapists) to align goals and strategies.",
    goal: "To create a consistent approach across all services and enhance the client’s overall progress.",
  },
  "Introduction of Natural Environment Teaching (NET)": {
    plan: "Include more Natural Environment Teaching (NET) in the next session, focusing on teaching skills in everyday activities.",
    goal: "To promote learning in a more natural and functional context, increasing the likelihood of generalization.",
  },
  "Fidelity Check for Treatment Implementation": {
    plan: "Conduct a fidelity check in the next session to evaluate the BT/RBT’s adherence to the treatment plan and provide additional coaching if needed.",
    goal: "To ensure high-quality implementation of the intervention and identify areas for improvement.",
  },
  "Monitoring Progress and Updating Goals": {
    plan: "Review the client’s progress based on the latest data and update the treatment goals as needed.",
    goal: "To keep the intervention plan dynamic and responsive to the client’s changing needs.",
  },
  Others: {
    focus:
      "Miscellaneous goals or activities specific to the client or program needs.",
    example:
      "Any additional observations or training tasks not categorized above.",
  },
};

export { sessionGoalAddressed, feedbackProvided, nextSessionPlans };
